import React from 'react';
import { Box, Chip, Paper, Stack, Table, TableBody, TableCell, TableRow, TextField, Typography } from '@mui/material';
import type { ImprestView } from '../../types/pettyCash';
import { formatAmount, imprestFigures, parseOptionalAmount, signedAmount } from '../../utils/pettyCash';
import { CheckList } from './AccountingView';

/**
 * The imprest check of a claim: the holder's float, less his earlier claims not yet reimbursed, this claim and the
 * advances and bills not yet claimed, gives the cash he should hold; he counts it and types it. Editable while the
 * holder may change the claim (saved with the claim), read-only after; the server freezes the figures at submission.
 */

/** The holder's typed figures (text, so "12." can be typed on the way to 12.5). */
export interface ImprestText {
  advances: string;
  cashInHand: string;
  note: string;
}

interface Props {
  view: ImprestView;
  editable: boolean;
  text: ImprestText;
  onChange: (next: ImprestText) => void;
  currency: string;
}

const ImprestCard: React.FC<Props> = ({ view, editable, text, onChange, currency }) => {
  const advances = editable ? parseOptionalAmount(text.advances) : { value: view.advances, ok: true };
  const cash = editable ? parseOptionalAmount(text.cashInHand) : { value: view.cashInHand, ok: true };
  const live = imprestFigures({
    floatAmount: view.floatAmount,
    outstanding: view.outstanding,
    thisClaim: view.thisClaim,
    advances: advances.value,
    cashInHand: cash.value,
    tolerance: view.tolerance,
    mode: view.checkMode,
  });
  // Typed figures are worked out here as the holder types; a saved claim shows the server's.
  const expected = editable ? live.expectedCash : view.expectedCash ?? live.expectedCash;
  const difference = editable ? live.difference : view.difference ?? live.difference;
  const beyond = difference !== null && Math.abs(difference) > (Number(view.tolerance) || 0) + 1e-9;
  const differenceColor = !beyond || view.checkMode === 'off' ? 'text.primary' : view.checkMode === 'block' ? 'error.main' : 'warning.dark';

  const amountField = (id: string, label: string, value: string, ok: boolean, key: 'advances' | 'cashInHand') => (
    <TextField
      id={id}
      size="small"
      value={value}
      onChange={(e) => onChange({ ...text, [key]: e.target.value.replace(/[^\d.,]/g, '').slice(0, 20) })}
      error={!ok}
      helperText={!ok ? 'Not an amount' : undefined}
      placeholder={key === 'advances' ? '0.00' : 'Count it'}
      inputProps={{ inputMode: 'decimal', 'aria-label': label, style: { textAlign: 'right' } }}
      sx={{ width: 150 }}
    />
  );
  const row = (label: string, value: React.ReactNode, strong = false) => (
    <TableRow>
      <TableCell component="th" scope="row" sx={{ pl: 0, fontWeight: strong ? 600 : 400, borderBottom: '1px solid #eceff1' }}>{label}</TableCell>
      <TableCell align="right" sx={{ pr: 0, fontWeight: strong ? 600 : 400, borderBottom: '1px solid #eceff1', whiteSpace: 'nowrap' }}>{value}</TableCell>
    </TableRow>
  );
  const shown = (n: number | null | undefined) => (n === null || n === undefined ? '—' : formatAmount(n));

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>Imprest check</Typography>
        {view.frozen ? <Chip size="small" variant="outlined" label="As at submission" /> : null}
        {view.floatAmount !== null && currency ? <Typography variant="caption" color="text.secondary">Amounts in {currency}</Typography> : null}
      </Stack>
      {view.floatAmount === null ? (
        <Typography variant="body2" color="text.secondary">No float is set for this holder</Typography>
      ) : (
        <Stack spacing={1.5}>
          <Table size="small" aria-label="Imprest check">
            <TableBody>
              {row('Float', shown(view.floatAmount))}
              {row('Less earlier claims not yet reimbursed', shown(view.outstanding))}
              {row('Less this claim', shown(view.thisClaim))}
              {row('Less advances and bills not yet claimed', editable
                ? amountField('pc-imprest-advances', 'Advances and bills not yet claimed', text.advances, advances.ok, 'advances')
                : shown(view.advances))}
              {row('Expected cash in hand', shown(expected), true)}
              {row('Cash in hand', editable
                ? amountField('pc-imprest-cash', 'Cash in hand', text.cashInHand, cash.ok, 'cashInHand')
                : shown(view.cashInHand), true)}
              {row('Difference', difference === null ? '—' : (
                <Box component="span" sx={{ color: differenceColor }}>{signedAmount(difference)}</Box>
              ))}
            </TableBody>
          </Table>
          {editable ? (
            <TextField
              id="pc-imprest-note"
              size="small"
              label="Note"
              value={text.note}
              onChange={(e) => onChange({ ...text, note: e.target.value.slice(0, 1000) })}
              helperText="Why the cash differs, or what the advances are"
              multiline
              minRows={1}
              fullWidth
            />
          ) : view.note ? (
            <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary' }}>Note: </Box>{view.note}</Typography>
          ) : null}
          {live.checks.length ? <CheckList checks={live.checks} /> : null}
          {editable ? (
            <Typography variant="caption" color="text.secondary">
              Count the cash you hold now and type it in Cash in hand. Advances are cash you handed out, or bills you paid that are on no claim
              yet. Saved with the claim.
            </Typography>
          ) : null}
        </Stack>
      )}
    </Paper>
  );
};

export default ImprestCard;
